import { datadogRum } from '@datadog/browser-rum'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { getCkoAlipayDialogMountId } from '@payments/lib/checkout-pay/ckoMountTarget'
import { useEffect, useState } from 'react'
import { PaymentCancelledError } from '../core/checkoutPayFlowController'
import { type AlipayDialogContext } from './use-alipay-dialog-flow-actions'
import { CKO_REDIRECT_APM_OPTION_ID_KEY } from './use-cko-redirect-apm-execution'

const MOUNT_TIMEOUT_MS = 30_000

export interface UseAlipayDialogFlowParams {
  context: AlipayDialogContext
  mountAlipayComponent: (context: AlipayDialogContext, containerId: string) => Promise<void>
  awaitFlowResult: (context: AlipayDialogContext) => Promise<void>
  cancelInFlight: (reason: string) => void
}

export const useAlipayDialogFlow = (props: UseAlipayDialogFlowParams) => {
  const { context, mountAlipayComponent, awaitFlowResult, cancelInFlight } = props
  const [isMounting, setIsMounting] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)
  const [retryNonce, setRetryNonce] = useState<number>(0)
  const containerId = getCkoAlipayDialogMountId(context.paymentTransactionId)

  const retry = useCallbackEvent(() => {
    setHasError(false)
    setRetryNonce(nonce => nonce + 1)
  })

  useEffect(function runAlipayFlow() {
    let isActive = true
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    setIsMounting(true)
    setHasError(false)

    const start = async () => {
      try {
        await Promise.race([
          mountAlipayComponent(context, containerId),
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new Error('[useAlipayDialogFlow] mount timed out')),
              MOUNT_TIMEOUT_MS,
            )
          }),
        ])
        if (!isActive) return
        setIsMounting(false)
        await awaitFlowResult(context)
      }
      catch (failure) {
        if (!isActive || failure instanceof PaymentCancelledError) return
        datadogRum.addError(failure instanceof Error ? failure : new Error(String(failure)), {
          function: 'useAlipayDialogFlow.runAlipayFlow',
          paymentTransactionId: context.paymentTransactionId,
          orderHash: context.orderHash,
        })
        setHasError(true)
      }
      finally {
        clearTimeout(timeoutId)
        if (isActive) setIsMounting(false)
      }
    }
    start()

    return () => {
      isActive = false
      clearTimeout(timeoutId)
      // SPA 內部關閉/導走（未按付款鈕直接離開）會走到這裡，清掉 stale bridge。
      // 按下 Flow 付款鈕是整頁 window.location 跳走、document teardown，不會執行此 cleanup，bridge 留給導回對帳。
      sessionStorage.removeItem(CKO_REDIRECT_APM_OPTION_ID_KEY)
    }
  }, [context, containerId, mountAlipayComponent, awaitFlowResult, retryNonce])

  // 獨立於流程 effect：只在 dialog 真正卸載時取消。retryNonce 變動重跑流程 effect 不該觸發此取消，
  // 否則 execution 階段失敗後按重新再試，會誤拆掉剛掛好的 SDK component。
  useEffect(function cancelOnDialogUnmount() {
    return () => {
      cancelInFlight('dialog unmounting')
    }
  }, [cancelInFlight])

  return {
    isMounting,
    hasError,
    containerId,
    retry,
  }
}
